import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ModalController, PopoverController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AlertManagementService } from 'src/app/pages/alert-management/alert-management.service';
import { NotificationsComponent } from 'src/app/header/notifications/notifications.component';
import { DateInterval } from 'src/app/model/date-interval.model';
import { SigninService } from 'src/app/signin/signin.service';
import { Router } from '@angular/router';
import { RealtimeWorkerService } from 'src/app/pages/realtime/realtime-worker.service';
import { DataManagementService } from 'src/app/pages/data-management/data-management.service';
import { HistoriqueMenuPopoverComponent } from '../historique-menu-popover/historique-menu-popover.component';
import { GraphicalReportMenuPopoverComponent } from '../graphical-report-menu-popover/graphical-report-menu-popover.component';
import { FuelDataComponent } from 'src/app/pages/statistical/fuel-data/fuel-data.component';
import { TechnicalDataComponent } from 'src/app/pages/statistical/technical-data/technical-data.component';
import { TempDataComponent } from 'src/app/pages/statistical/temp-data/temp-data.component';
@Component({
  selector: 'app-rt-bottom-nav',
  templateUrl: './rt-bottom-nav.component.html',
  styleUrls: ['./rt-bottom-nav.component.scss'],
})
export class RtBottomNavComponent implements OnInit, OnDestroy {
  @Input() historiqueActive = false;
  @Input() realtimeActive = false;
  @Output() centerMenuClick = new EventEmitter<void>();
  @Output() realtimeSelected = new EventEmitter<void>();

  notifications: any[] = [];
  dateInterval: DateInterval = new DateInterval();
  private destroy$ = new Subject<void>();
  private liveReloadingNotif: any;
  private openPopover: HTMLIonPopoverElement | null = null;

  constructor(
    public signinService: SigninService,
    private alertManagementService: AlertManagementService,
    private dataManagementService: DataManagementService,
    private modalCtrl: ModalController,
    private popoverCtrl: PopoverController,
    private router: Router,
    private realTimeWorker: RealtimeWorkerService,
  ) {}

  ngOnInit(): void {
    this.initTimerNotif();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.liveReloadingNotif) {
      clearInterval(this.liveReloadingNotif);
    }
    void this.dismissOpenPopover();
  }

  onRealtimeClick(): void {
    void this.dismissOpenPopover();
    this.realtimeSelected.emit();
    if (!this.realtimeActive) {
      this.router.navigate(['/client']);
    }
  }

  async openHistoriqueMenu(ev: Event): Promise<void> {
    ev.preventDefault();
    ev.stopPropagation();
    await this.dismissOpenPopover();

    const popover = await this.popoverCtrl.create({
      component: HistoriqueMenuPopoverComponent,
      event: ev,
      side: 'top',
      alignment: 'center',
      cssClass: this.navPopoverCssClass(),
      backdropDismiss: true,
      showBackdrop: true,
      translucent: false,
      componentProps: {
        showVideoPlayback: this.signinService.hasVehicleCameraOption(
          this.dataManagementService.selectedVehicule
        ),
      },
    });

    this.openPopover = popover;
    await popover.present();
    await popover.onDidDismiss();
    this.openPopover = null;
  }

  async openRapportMenu(ev: Event): Promise<void> {
    ev.preventDefault();
    ev.stopPropagation();
    await this.dismissOpenPopover();

    const popover = await this.popoverCtrl.create({
      component: GraphicalReportMenuPopoverComponent,
      event: ev,
      side: 'top',
      alignment: 'center',
      cssClass: this.navPopoverCssClass(),
      backdropDismiss: true,
      showBackdrop: true,
      translucent: false,
      componentProps: {
        showFuel: this.signinService.hasVehicleFuelOption(
          this.dataManagementService.selectedVehicule
        ),
        showTemperature: this.signinService.hasVehicleTemperatureOption(
          this.dataManagementService.selectedVehicule
        ),
      },
    });

    this.openPopover = popover;
    await popover.present();
    const { data } = await popover.onDidDismiss();
    this.openPopover = null;

    if (data?.action === 'technical') {
      await this.openTechnicalModal();
    } else if (data?.action === 'fuel') {
      await this.openCarburantModal();
    } else if (data?.action === 'temperature') {
      await this.openTemperatureModal();
    }
  }

  async clickNotifications(ev: Event): Promise<void> {
    await this.dismissOpenPopover();
    this.realTimeWorker.prepareMapForOverlay();
    const dialog = await this.modalCtrl.create({
      component: NotificationsComponent,
      cssClass: 'notifDialog',
      showBackdrop: true,
      componentProps: { notifications: this.notifications },
    });
    await dialog.present();
    await dialog.onDidDismiss();
    this.realTimeWorker.restoreMapAfterOverlay();
  }

  private async openCarburantModal(): Promise<void> {
    this.realTimeWorker.prepareMapForOverlay();
    const modal = await this.modalCtrl.create({
      component: FuelDataComponent,
      cssClass: 'fuel-overlay-modal',
      backdropDismiss: true,
      showBackdrop: false,
      componentProps: {
        overlayOnly: true,
      },
    });
    await modal.present();
    this.realTimeWorker.refreshMapUnderOverlay();
    await modal.onDidDismiss();
    this.realTimeWorker.restoreMapAfterOverlay();
  }

  private async openTechnicalModal(): Promise<void> {
    this.realTimeWorker.prepareMapForOverlay();
    const modal = await this.modalCtrl.create({
      component: TechnicalDataComponent,
      cssClass: 'fuel-overlay-modal',
      backdropDismiss: true,
      showBackdrop: false,
      componentProps: {
        overlayOnly: true,
      },
    });
    await modal.present();
    this.realTimeWorker.refreshMapUnderOverlay();
    await modal.onDidDismiss();
    this.realTimeWorker.restoreMapAfterOverlay();
  }

  private async openTemperatureModal(): Promise<void> {
    this.realTimeWorker.prepareMapForOverlay();
    const modal = await this.modalCtrl.create({
      component: TempDataComponent,
      cssClass: 'fuel-overlay-modal',
      backdropDismiss: true,
      showBackdrop: false,
      componentProps: {
        overlayOnly: true,
      },
    });
    await modal.present();
    this.realTimeWorker.refreshMapUnderOverlay();
    await modal.onDidDismiss();
    this.realTimeWorker.restoreMapAfterOverlay();
  }

  private async dismissOpenPopover(): Promise<void> {
    if (this.openPopover) {
      await this.openPopover.dismiss().catch(() => undefined);
      this.openPopover = null;
    }
  }

  private navPopoverCssClass(): string {
    const isDark = document.body.classList.contains('dark-theme');
    return isDark ? 'rt-nav-popover-sheet rt-nav-popover-sheet--dark' : 'rt-nav-popover-sheet';
  }

  private loadNotification(): void {
    this.alertManagementService
      .getNotifications(this.dateInterval)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (notifications) => {
          this.notifications = notifications;
        },
        error: (err) => console.error('Error loading notifications:', err),
      });
  }

  private initTimerNotif(): void {
    if (this.liveReloadingNotif) {
      clearInterval(this.liveReloadingNotif);
    }
    this.loadNotification();
    this.liveReloadingNotif = setInterval(() => this.loadNotification(), 20000);
  }
}
